import { useQuery } from 'hooks'
import { useEffect, useMemo, useState } from 'react'
import { fetchOrdersLogs } from 'utils/api/logsApi'
import { calculateOffset } from 'utils/helpers/pagination'
import { getColumns } from './utils'
import DataTable from 'components/common/dataTable'
import { DataTablePagination, Dropdown } from 'components/common'
import { useLocation, useNavigate } from 'react-router-dom'

const ORDER_STATUS = {
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
} as const

const OrderLogTable = () => {
  const [logResponse, setLogResponse] = useState<any>({})
  const [page, setPage] = useState<number>(0)
  const [availableTypes, setAvailableTypes] = useState<string[]>([])

  const { getQuery } = useQuery()
  const { search } = useLocation()
  const navigate = useNavigate()

  const [orderNo, setOrderNo] = useState<string>(getQuery('orderNo') || '')

  const columns = useMemo(() => getColumns(page), [page])

  const getLogs = async () => {
    try {
      const { data } = await fetchOrdersLogs({
        limit: getQuery('limit') || 10,
        page: getQuery('page') || 1,
        type: getQuery('type') || undefined,
        status: getQuery('status') || undefined,
        orderNo: getQuery('orderNo') || undefined,
        startDate: getQuery('startDate') || undefined,
        endDate: getQuery('endDate') || undefined,
      })

      setPage(calculateOffset(data))
      setLogResponse(data)
      setAvailableTypes(data.availableTypes || [])
    } catch (error) {
      setLogResponse({ docs: [] })
      setAvailableTypes([])
    }
  }

  useEffect(() => {
    getLogs()
  }, [search])

  /* ---------------- TYPE OPTIONS ---------------- */

  const typeOptions = useMemo(() => {
    const actualOptions = availableTypes.map((type) => ({
      label: type,
      value: type,
    }))

    return [{ label: '-- NONE --', value: '' }, ...actualOptions]
  }, [availableTypes])

  /* ---------------- STATUS OPTIONS ---------------- */

  const statusOptions = useMemo(() => {
    const actualOptions = Object.values(ORDER_STATUS).map((status) => ({
      label: status,
      value: status,
    }))

    return [{ label: '-- NONE --', value: '' }, ...actualOptions]
  }, [])

  /* ---------------- FIXED QUERY PARAM HANDLING ---------------- */

  const updateQueryParam = (key: string, value: string) => {
    const params = new URLSearchParams(window.location.search)

    if (!value) {
      params.delete(key)
    } else {
      params.set(key, value)
    }

    params.set('page', '1')

    navigate({
      pathname: '/orders-logs',
      search: params.toString(),
    })
  }

  const handleTypeChange = (_fieldName: string, value: string) => {
    updateQueryParam('type', value)
  }

  const handleStatusChange = (_fieldName: string, value: string) => {
    updateQueryParam('status', value)
  }

  const applyOrderNo = () => {
    updateQueryParam('orderNo', orderNo.trim())
  }

  const handleStartDateChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    updateQueryParam('startDate', event.target.value)
  }

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateQueryParam('endDate', event.target.value)
  }

  const clearFilter = () => {
    setOrderNo('')
    navigate('/orders-logs')
  }

  const hasActiveFilter =
    getQuery('type') ||
    getQuery('status') ||
    getQuery('orderNo') ||
    getQuery('startDate') ||
    getQuery('endDate')

  return (
    <>
      <h2 className="mb-6 text-2xl font-semibold text-gray-800">Orders Logs</h2>

      <div className="mb-6 flex flex-wrap items-end gap-6 rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
        <div className="min-w-[220px]">
          <Dropdown
            options={typeOptions}
            name="type"
            label="Order Type"
            defaultValue={getQuery('type') || ''}
            setValue={handleTypeChange}
          />
        </div>

        <div className="min-w-[220px]">
          <Dropdown
            options={statusOptions}
            name="status"
            label="Status"
            defaultValue={getQuery('status') || ''}
            setValue={handleStatusChange}
          />
        </div>

        <div className="min-w-[200px]">
          <label className="mb-1 block text-xs font-semibold text-sidebar-primary">
            Order No
          </label>
          <input
            type="text"
            value={orderNo}
            placeholder="Search order no..."
            onChange={(event) => setOrderNo(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && applyOrderNo()}
            onBlur={applyOrderNo}
            className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-primary focus:border-2 focus:border-gray-500 focus:outline-none"
          />
        </div>

        <div className="min-w-[170px]">
          <label className="mb-1 block text-xs font-semibold text-sidebar-primary">
            Start Date
          </label>
          <input
            type="date"
            value={getQuery('startDate') || ''}
            onChange={handleStartDateChange}
            className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-primary focus:border-2 focus:border-gray-500 focus:outline-none"
          />
        </div>

        <div className="min-w-[170px]">
          <label className="mb-1 block text-xs font-semibold text-sidebar-primary">
            End Date
          </label>
          <input
            type="date"
            value={getQuery('endDate') || ''}
            onChange={handleEndDateChange}
            className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-primary focus:border-2 focus:border-gray-500 focus:outline-none"
          />
        </div>

        {hasActiveFilter && (
          <button
            onClick={clearFilter}
            className="mb-[2px] rounded-md border-2 p-3 text-sm font-medium text-gray-500 transition-colors duration-200 hover:text-gray-900"
          >
            Clear
          </button>
        )}
      </div>

      <DataTable columns={columns} rows={logResponse?.docs} identifier="_id" />

      <DataTablePagination
        disableGoToLastPage
        shouldDisplayTotalPages={false}
        currentPage={logResponse?.page}
        totalPages={logResponse?.totalPages}
        hasNextPage={logResponse?.hasNextPage}
        hasPrevPage={logResponse?.hasPrevPage}
        limit={logResponse?.limit}
        nextPage={logResponse?.nextPage}
        prevPage={logResponse?.prevPage}
      />
    </>
  )
}

export default OrderLogTable
